/**
 * ModulesOverviewTab — zawartość zakładki „Moduły" w Ustawieniach tenanta.
 *
 * Zamiast kart-linków prowadzących na osobne strony, pokazuje OD RAZU listę modułów:
 *   - sekcja „Moduły systemowe" (góra) — GET /system-modules, klik → /system-modules/:slug/settings
 *   - sekcja „Moduły własne" (dół)     — GET /modules,        klik → /modules/:id/builder
 *
 * Pełne strony (/system-modules, /modules) zostają — to tylko skrót w Ustawieniach.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Copy, Plus, Search, Trash2 } from 'lucide-react';

import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type CreatedKartoteka,
  type CreateKartotekaInput,
  CreateKartotekaModal,
} from '@/components/kartoteki/CreateKartotekaModal';
import { renderIconByName } from '@/components/modules/IconPicker';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { useReservedFieldSlugToast } from '@/hooks/useReservedFieldSlugToast';
import { api } from '@/lib/api';

/** Powiązanie zwracane przez /modules/:id/deletion-impact. */
interface DeletionImpactReference {
  type: 'module' | 'process';
  label: string;
}
interface DeletionImpact {
  recordCount: number;
  references: DeletionImpactReference[];
}
/** Kartoteka wybrana do usunięcia + policzony wpływ (pokazywany w modalu). */
interface PendingDelete {
  id: string;
  name: string;
  impact: DeletionImpact | null;
}

interface SystemModuleListItem {
  slug: string;
  name: string;
  entityTableName: string;
  icon: string;
  color: string;
}

interface CustomModuleListItem {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  /**
   * Pochodzenie kartoteki: NIE-null = zainstalowana z szablonu huba (od super admina)
   * → dla tenanta jest SYSTEMOWA. null = stworzona ręcznie przez tenanta → WŁASNA.
   */
  sourceTemplateModuleId?: string | null;
}

function ModuleCard({
  name,
  icon,
  color,
  subtitle,
  badge,
  onClick,
  onDuplicate,
  onDelete,
  duplicating,
  testId,
}: {
  name: string;
  icon: string;
  color: string;
  subtitle?: string;
  badge?: string;
  onClick: () => void;
  /** Duplikuj — dostępne na każdym kaflu (systemowe i własne). */
  onDuplicate?: () => void;
  /** Usuń — tylko kartoteki własne tenanta. */
  onDelete?: () => void;
  duplicating?: boolean;
  testId: string;
}) {
  const { t } = useTranslation();
  return (
    <Card
      padding="md"
      className="group relative cursor-pointer transition hover:border-accent-500 hover:shadow-md"
      onClick={onClick}
      data-testid={testId}
    >
      {/* Akcje kafla — pojawiają się na hover/focus; stopPropagation, by nie odpalić onClick karty. */}
      {(onDuplicate || onDelete) && (
        <div className="absolute right-2 top-2 flex items-center gap-1">
          {onDuplicate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              disabled={duplicating}
              aria-label={t('tenant.settings.modulesOverview.actions.duplicate')}
              title={t('tenant.settings.modulesOverview.actions.duplicate')}
              className="rounded-md p-1.5 text-text-muted transition hover:bg-bg-tertiary hover:text-accent-600 disabled:opacity-50"
              data-testid={`${testId}-duplicate`}
            >
              <Copy className="h-4 w-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              aria-label={t('tenant.settings.modulesOverview.actions.delete')}
              title={t('tenant.settings.modulesOverview.actions.delete')}
              className="rounded-md p-1.5 text-text-muted transition hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-900/20"
              data-testid={`${testId}-delete`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${color}20`, color }}
          aria-hidden="true"
        >
          {renderIconByName(icon, 'h-5 w-5')}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="truncate font-semibold text-text-primary">{name}</h3>
            {badge && (
              <span className="shrink-0 rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted">
                {badge}
              </span>
            )}
          </div>
          {subtitle && <p className="mt-1 truncate text-xs text-text-tertiary">{subtitle}</p>}
        </div>
      </div>
    </Card>
  );
}

export function ModulesOverviewTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const showReservedSlugToast = useReservedFieldSlugToast();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const systemQuery = useQuery({
    queryKey: ['system-modules'],
    queryFn: async () => {
      const res = await api.get<{ modules: SystemModuleListItem[] }>('/system-modules');
      return res.data.modules;
    },
  });

  const customQuery = useQuery({
    queryKey: ['modules', 'overview'],
    queryFn: async () => {
      const res = await api.get<{ modules: CustomModuleListItem[] }>('/modules');
      return res.data.modules;
    },
  });

  // Wszystko co pochodzi od super admina jest dla tenanta SYSTEMOWE — także kartoteki
  // zainstalowane z szablonu huba (mają `sourceTemplateModuleId`). Tylko kartoteki
  // stworzone ręcznie przez tenanta (bez źródła szablonu) są WŁASNE.
  const customData = customQuery.data ?? [];
  const installedFromTemplate = customData.filter((m) => m.sourceTemplateModuleId != null);
  const tenantCreated = customData.filter((m) => m.sourceTemplateModuleId == null);

  // Slugi wszystkich widocznych kartotek — kliencka podpowiedź „już zajęty" w modalu.
  const existingSlugs = [
    ...(systemQuery.data ?? []).map((m) => m.slug),
    ...customData.map((m) => m.slug),
  ];

  // Zapis tenant-scope (POST /modules). Sama walidacja slugu żyje we wspólnym
  // module-identity (@saas/shared) — modal jest współdzielony z adminem.
  const createKartoteka = async (input: CreateKartotekaInput): Promise<CreatedKartoteka> => {
    const res = await api.post<{ module: CreatedKartoteka }>('/modules', input);
    return res.data.module;
  };

  const handleCreated = (mod: CreatedKartoteka) => {
    queryClient.invalidateQueries({ queryKey: ['modules'] });
    queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
    if (mod.id) navigate(`/modules/${mod.id}/builder`);
  };

  // ── Duplikacja ──────────────────────────────────────────────────────────────
  const afterDuplicate = (mod: CreatedKartoteka) => {
    toast.show({
      variant: 'success',
      title: t('tenant.settings.modulesOverview.toasts.duplicated'),
    });
    queryClient.invalidateQueries({ queryKey: ['modules'] });
    queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
    if (mod.id) navigate(`/modules/${mod.id}/builder`);
  };
  const onDuplicateError = (err: unknown) => {
    // 400 FIELD_SLUG_RESERVED dotyczy kopii przez `POST /modules/:id/duplicate` — kartoteki własnej
    // I zainstalowanej z szablonu (obie karty wołają tę samą mutację); kopia kartoteki systemowej
    // (`/system-modules/:slug/duplicate`) przemianowuje takie pole. Pole źródła ma nazwę techniczną
    // zarezerwowaną przez system (np. „decisions"); aplikacja jej nie zmienia, więc komunikat
    // kieruje do opiekuna i zostaje do ręcznego zamknięcia (wspólny hak).
    const title = t('tenant.settings.modulesOverview.toasts.duplicateError');
    if (showReservedSlugToast(err, { title, variant: 'support' })) return;
    toast.show({ variant: 'error', title });
  };

  const duplicateCustomMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      const res = await api.post<{ module: CreatedKartoteka }>(`/modules/${moduleId}/duplicate`);
      return res.data.module;
    },
    onSuccess: afterDuplicate,
    onError: onDuplicateError,
  });
  const duplicateSystemMutation = useMutation({
    mutationFn: async (slug: string) => {
      const res = await api.post<{ module: CreatedKartoteka }>(`/system-modules/${slug}/duplicate`);
      return res.data.module;
    },
    onSuccess: afterDuplicate,
    onError: onDuplicateError,
  });

  // ── Usuwanie (miękkie, odwracalne) ────────────────────────────────────────────
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const openDelete = async (mod: { id: string; name: string }) => {
    // Pokaż modal od razu; „co zniknie" doładuj asynchronicznie.
    setPendingDelete({ id: mod.id, name: mod.name, impact: null });
    try {
      const res = await api.get<DeletionImpact>(`/modules/${mod.id}/deletion-impact`);
      setPendingDelete((cur) => (cur && cur.id === mod.id ? { ...cur, impact: res.data } : cur));
    } catch {
      // Brak wpływu = pokazujemy sam komunikat odwracalności (nie blokujemy usunięcia).
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (moduleId: string) => {
      await api.delete(`/modules/${moduleId}`);
      return moduleId;
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.settings.modulesOverview.toasts.deleted'),
      });
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      queryClient.invalidateQueries({ queryKey: ['modules', 'menu'] });
      setPendingDelete(null);
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.settings.modulesOverview.toasts.deleteError'),
      });
      setPendingDelete(null);
    },
  });

  // Komunikat modalu usuwania: liczba rekordów + powiązania + info o odwracalności.
  const deleteMessage = (() => {
    if (!pendingDelete) return '';
    const parts = [
      t('tenant.settings.modulesOverview.delete.confirm', { name: pendingDelete.name }),
    ];
    const impact = pendingDelete.impact;
    if (impact) {
      if (impact.recordCount > 0) {
        parts.push(
          t('tenant.settings.modulesOverview.delete.records', { count: impact.recordCount }),
        );
      }
      if (impact.references.length > 0) {
        parts.push(
          t('tenant.settings.modulesOverview.delete.relations', {
            list: impact.references.map((r) => r.label).join(', '),
          }),
        );
      }
    }
    parts.push(t('tenant.settings.modulesOverview.delete.reversible'));
    return parts.join(' ');
  })();

  // Wyszukiwarka — filtruje wszystkie trzy grupy po nazwie ORAZ slugu (bez rozróżniania wielkości).
  const phrase = search.trim().toLowerCase();
  const matches = (m: { name: string; slug: string }) =>
    !phrase || m.name.toLowerCase().includes(phrase) || m.slug.toLowerCase().includes(phrase);
  const systemFiltered = (systemQuery.data ?? []).filter(matches);
  const installedFiltered = installedFromTemplate.filter(matches);
  const createdFiltered = tenantCreated.filter(matches);
  const noSearchHits =
    phrase.length > 0 &&
    systemFiltered.length === 0 &&
    installedFiltered.length === 0 &&
    createdFiltered.length === 0;

  return (
    <div className="flex flex-col gap-8" data-testid="settings-modules-overview">
      {/* Wyszukiwarka kartotek */}
      <div className="relative max-w-sm">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('tenant.settings.modulesOverview.searchPlaceholder')}
          aria-label={t('tenant.settings.modulesOverview.searchPlaceholder')}
          className="w-full rounded-md border border-border-primary bg-bg-primary py-2 pl-9 pr-3 text-sm text-text-primary"
          data-testid="modules-overview-search"
        />
      </div>

      {noSearchHits && (
        <p
          className="rounded-lg border border-dashed border-border-primary p-6 text-center text-sm text-text-muted"
          data-testid="modules-overview-no-results"
        >
          {t('tenant.settings.modulesOverview.noResults', { phrase: search.trim() })}
        </p>
      )}
      {/* ── Moduły systemowe (góra) ─────────────────────────────────────── */}
      {(!phrase || systemFiltered.length > 0 || installedFiltered.length > 0) && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted">
            {t('tenant.settings.modulesOverview.systemSection')}
          </h2>
          {systemQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : systemQuery.isError ? (
            <Card padding="md">
              <p className="text-error-500">
                {isAxiosError(systemQuery.error) && systemQuery.error.response?.status === 403
                  ? t('tenant.systemModules.forbidden')
                  : t('tenant.systemModules.loadError')}
              </p>
            </Card>
          ) : systemFiltered.length === 0 ? (
            phrase ? null : (
              <p className="text-sm text-text-muted">
                {t('tenant.settings.modulesOverview.systemEmpty')}
              </p>
            )
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {systemFiltered.map((mod) => (
                <ModuleCard
                  key={mod.slug}
                  name={mod.name}
                  icon={mod.icon}
                  color={mod.color}
                  subtitle={`/${mod.slug}`}
                  badge={t('tenant.systemModules.coreBadge')}
                  onClick={() => navigate(`/system-modules/${mod.slug}/settings`)}
                  onDuplicate={() => duplicateSystemMutation.mutate(mod.slug)}
                  duplicating={
                    duplicateSystemMutation.isPending &&
                    duplicateSystemMutation.variables === mod.slug
                  }
                  testId={`overview-system-module-${mod.slug}`}
                />
              ))}
            </div>
          )}

          {/* Kartoteki zainstalowane z szablonu huba — pochodzą od super admina, więc
            dla tenanta są systemowe (choć technicznie to custom_modules → builder). */}
          {installedFiltered.length > 0 ? (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {installedFiltered.map((mod) => (
                <ModuleCard
                  key={mod.id}
                  name={mod.name}
                  icon={mod.icon ?? 'Database'}
                  color={mod.color ?? '#3B82F6'}
                  subtitle={`/${mod.slug}`}
                  onClick={() => navigate(`/modules/${mod.id}/builder`)}
                  onDuplicate={() => duplicateCustomMutation.mutate(mod.id)}
                  duplicating={
                    duplicateCustomMutation.isPending &&
                    duplicateCustomMutation.variables === mod.id
                  }
                  testId={`overview-installed-module-${mod.slug}`}
                />
              ))}
            </div>
          ) : null}
        </section>
      )}

      {/* ── Kartoteki własne (dół) ──────────────────────────────────────── */}
      {(!phrase || createdFiltered.length > 0) && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
              {t('tenant.settings.modulesOverview.customSection')}
            </h2>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-primary px-2.5 py-1.5 text-xs font-medium text-text-secondary transition hover:border-accent-500 hover:text-accent-600"
              data-testid="overview-create-custom-btn"
            >
              <Plus className="h-4 w-4" />
              {t('tenant.settings.modulesOverview.createCustom')}
            </button>
          </div>
          {customQuery.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : customQuery.isError ? (
            <Card padding="md">
              <p className="text-error-500">{t('tenant.modules.loadError')}</p>
            </Card>
          ) : createdFiltered.length === 0 ? (
            phrase ? null : (
              <p className="text-sm text-text-muted">
                {t('tenant.settings.modulesOverview.customEmpty')}
              </p>
            )
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {createdFiltered.map((mod) => (
                <ModuleCard
                  key={mod.id}
                  name={mod.name}
                  icon={mod.icon ?? 'Database'}
                  color={mod.color ?? '#3B82F6'}
                  subtitle={`/${mod.slug}`}
                  onClick={() => navigate(`/modules/${mod.id}/builder`)}
                  onDuplicate={() => duplicateCustomMutation.mutate(mod.id)}
                  duplicating={
                    duplicateCustomMutation.isPending &&
                    duplicateCustomMutation.variables === mod.id
                  }
                  onDelete={() => openDelete(mod)}
                  testId={`overview-custom-module-${mod.slug}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      <CreateKartotekaModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        submitFn={createKartoteka}
        onCreated={handleCreated}
        existingSlugs={existingSlugs}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t('tenant.settings.modulesOverview.delete.title')}
        message={deleteMessage}
        confirmLabel={t('tenant.settings.modulesOverview.actions.delete')}
        confirming={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
